import { supabase } from '../lib/supabase';

export interface DiscountCodeValidation {
  isValid: boolean;
  code?: {
    id: string;
    code: string;
    description?: string;
    type: 'percentage' | 'fixed';
    value: number;
    min_order_amount?: number;
    max_uses?: number;
    used_count: number;
    expires_at?: string;
    is_active: boolean;
  };
  error?: string;
  discountAmount?: number;
}

export class DiscountCodeService {
  /**
   * Validate a discount code for a specific business and order amount.
   * Uses a SECURITY DEFINER RPC so online booking (anon) can validate
   * without needing SELECT access on discount_codes.
   */
  static async validateDiscountCode(
    businessId: string,
    code: string,
    orderAmount: number
  ): Promise<DiscountCodeValidation> {
    try {
      const { data, error } = await supabase.rpc('validate_public_discount_code', {
        p_business_id: businessId,
        p_code: code,
        p_order_amount: orderAmount
      });

      if (error) {
        console.error('Error validating discount code:', error);
        return {
          isValid: false,
          error: 'Failed to validate discount code. Please try again.'
        };
      }

      const result = data as {
        is_valid?: boolean;
        error?: string;
        discount_amount?: number;
        code?: DiscountCodeValidation['code'];
      } | null;

      if (!result?.is_valid || !result.code) {
        return {
          isValid: false,
          error: result?.error || 'Invalid discount code'
        };
      }

      return {
        isValid: true,
        code: {
          ...result.code,
          type: result.code.type as 'percentage' | 'fixed',
          value: Number(result.code.value),
          used_count: Number(result.code.used_count || 0),
          min_order_amount: result.code.min_order_amount != null
            ? Number(result.code.min_order_amount)
            : undefined,
          max_uses: result.code.max_uses != null
            ? Number(result.code.max_uses)
            : undefined,
        },
        discountAmount: Number(result.discount_amount || 0)
      };
    } catch (error) {
      console.error('Error validating discount code:', error);
      return {
        isValid: false,
        error: 'Failed to validate discount code. Please try again.'
      };
    }
  }

  /**
   * Apply a discount code to a booking.
   * Uses a SECURITY DEFINER RPC so online booking (anon) can apply codes
   * without needing write access on discount_codes / booking_discounts.
   */
  static async applyDiscountCode(
    bookingId: string,
    businessId: string,
    code: string,
    orderAmount: number
  ): Promise<{ success: boolean; error?: string; discount?: any }> {
    try {
      const { data, error } = await supabase.rpc('apply_public_discount_code', {
        p_booking_id: bookingId,
        p_business_id: businessId,
        p_code: code,
        p_order_amount: orderAmount
      });

      if (error) {
        console.error('Error applying discount code:', error);
        return {
          success: false,
          error: 'Failed to apply discount code. Please try again.'
        };
      }

      const result = data as {
        success?: boolean;
        error?: string;
        discount?: any;
      } | null;

      if (!result?.success) {
        return {
          success: false,
          error: result?.error || 'Failed to apply discount code. Please try again.'
        };
      }

      return {
        success: true,
        discount: result.discount
      };
    } catch (error) {
      console.error('Error applying discount code:', error);
      return {
        success: false,
        error: 'Failed to apply discount code. Please try again.'
      };
    }
  }

  /**
   * Get all discount codes for a business (for admin use)
   */
  static async getBusinessDiscountCodes(businessId: string) {
    const { data, error } = await supabase
      .from('discount_codes')
      .select('*')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching discount codes:', error);
      return [];
    }

    return data || [];
  }
}
